import React, { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Card, Button, Form, Select, Typography, Flex, Tag, Spin } from "antd"
import styles from "./index.module.css"
import { CircledClose } from "ui/Icon/DSIcon/CircledClose"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { CustomForm } from "ui/Form"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { getAllGuideDigitalWaivers } from "store/digital-waiver"
import { updateTrip, getGuideTrip } from "store/trips"
import { useParams } from "react-router-dom"
import { routes } from "router"
import { setFormTouched } from "store/proNewListingForms"
import { DocumentsAddedModal } from "pagesV2/pro/DigitalWaiverAndContracts/DocumentsAdded/modal/DocumentsAdded"
import { toaster } from "components/common/Toast"
import { useDevice } from "hooks/useDevice"
const { Title, Text } = Typography

const WaiverOptionRender = (option) => (
  <Flex align="center" gap={8}>
    {option.label}
    {!option.data.isActive && <Tag color="default">Inactive</Tag>}
  </Flex>
)

const WaiverTagRender = ({ label, value, closable, onRemove }) => (
  <Tag
    className={styles.categoryChip}
    key={value}
    closable={closable}
    onClose={(event) => onRemove(value, event)}
    closeIcon={<CircledClose width={16} height={16} fill="var(--color-primary)" />}
  >
    {label}
  </Tag>
)

WaiverTagRender.propTypes = {
  label: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  closable: PropTypes.bool,
  onRemove: PropTypes.func.isRequired
}

WaiverTagRender.defaultProps = {
  label: null,
  value: "",
  closable: false
}

const ListingDigitalWaiver = () => {
  const { id } = useParams()
  const [t] = useTranslation()
  const { isMobile } = useDevice()
  const [form] = Form.useForm()
  const [digitalWaivers, setDigitalWaivers] = useState([])
  const [selectedWaivers, setSelectedWaivers] = useState([])
  const [errorMessage, setErrorMessage] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { items, loading } = useSelector((state) => state.digitalWaiver.allWaivers)
  const dropdownOptions = items.map((item) => ({
    label: item.name,
    value: item.id,
    disabled: !item.is_active,
    isActive: item.is_active
  }))
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(getGuideTrip(id)).then((res) => {
      setDigitalWaivers(res.data.digital_waiver_experiences || [])
    })
  }, [dispatch, id])
  useEffect(() => {
    if (digitalWaivers.length > 0) {
      const selected = digitalWaivers.map((dw) => dw.digital_waiver_id)
      setSelectedWaivers(selected)
      form.setFieldsValue({ waivers: selected })
    }
  }, [digitalWaivers, form])
  useEffect(() => {
    dispatch(getAllGuideDigitalWaivers())
  }, [dispatch])
  const onFinish = (callback) => {
    form
      .validateFields()
      .then(() => processFormSubmission(callback))
      .catch((error_) => {
        console.log("Validation failed:", error_)
      })
  }
  const processFormSubmission = (callback) => {
    const values = form.getFieldsValue()
    const selectedWaiverIds = values.waivers || []
    if (selectedWaiverIds.length > 5) {
      setErrorMessage("You can select up to 5 waivers only.")
      return
    } else {
      setErrorMessage("")
    }
    const formData = new FormData()

    const existingWaiversMap = {}
    digitalWaivers.forEach((waiver) => {
      existingWaiversMap[waiver.digital_waiver_id] = waiver.id
    })

    let formIndex = 0

    selectedWaiverIds.forEach((digitalWaiverId) => {
      if (existingWaiversMap[digitalWaiverId]) {
        formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][id]`, existingWaiversMap[digitalWaiverId])
      } else {
        formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][digital_waiver_id]`, digitalWaiverId)
      }
      formIndex++
    })

    digitalWaivers.forEach((waiver) => {
      if (!selectedWaiverIds.includes(waiver.digital_waiver_id)) {
        formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][id]`, waiver.id)
        formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][_destroy]`, "true")
        formIndex++
      }
    })

    formData.append("trip[wizard_step]", "digital_waivers")

    console.log("FormData entries:")
    for (let pair of formData.entries()) {
      console.log(pair[0] + ": " + pair[1])
    }

    dispatch(updateTrip(id, formData))
    if (callback) callback()
  }

  useEffect(() => {
    const handleSaveRequest = async (event) => {
      const { resolve, reject } = event.detail
      try {
        const selectedWaiverIds = form.getFieldValue("waivers") || []
        const formData = new FormData()
        const existingWaiversMap = {}
        digitalWaivers.forEach((waiver) => {
          existingWaiversMap[waiver.digital_waiver_id] = waiver.id
        })
        let formIndex = 0
        selectedWaiverIds.forEach((digitalWaiverId) => {
          if (existingWaiversMap[digitalWaiverId]) {
            formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][id]`, existingWaiversMap[digitalWaiverId])
          } else {
            formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][digital_waiver_id]`, digitalWaiverId)
          }
          formIndex++
        })
        digitalWaivers.forEach((waiver) => {
          if (!selectedWaiverIds.includes(waiver.digital_waiver_id)) {
            formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][id]`, waiver.id)
            formData.append(`trip[digital_waiver_experiences_attributes][${formIndex}][_destroy]`, "true")
            formIndex++
          }
        })
        formData.append("trip[wizard_step]", "digital_waivers")
        await dispatch(updateTrip(id, formData))
        dispatch(setFormTouched("digital_waiver", false))
        resolve()
      } catch (error) {
        reject(error)
      }
    }
    window.addEventListener("waiverSaveRequest", handleSaveRequest)
    return () => window.removeEventListener("waiverSaveRequest", handleSaveRequest)
  }, [dispatch, id, form, digitalWaivers])

  const handleValuesChange = (changedValues, allValues) => {}

  const handleWaiverTagRemove = useCallback(
    (value, event) => {
      setErrorMessage("")
      event.preventDefault()
      const currentWaivers = form.getFieldValue("waivers") || []
      const newWaivers = currentWaivers.filter((item) => item !== value)
      form.setFieldsValue({ waivers: newWaivers })
      setSelectedWaivers(newWaivers)
      dispatch(setFormTouched("digital_waiver", true))
    },
    [dispatch, form]
  )

  const renderWaiverTag = useCallback(
    (tagProps) => <WaiverTagRender {...tagProps} onRemove={handleWaiverTagRemove} />,
    [handleWaiverTagRemove]
  )

  const handleAddDigitalWaiver = () => {
    // Change URL without triggering React Router navigation so the page stays visible
    window.history.pushState(null, "", `/#${routes.ProDigitalWaiverNew()}`)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    window.history.back()
  }

  const handleModalSave = () => {
    setIsModalOpen(false)
    dispatch(getAllGuideDigitalWaivers())
    dispatch(getGuideTrip(id)).then((res) => {
      setDigitalWaivers(res.data.digital_waiver_experiences || [])
    })
    window.history.back()
  }

  if (isMobile && isModalOpen) {
    return (
      <Spin spinning={loading}>
        <DocumentsAddedModal
          isLoading={false}
          setIsLoading={() => {}}
          visible={isModalOpen}
          onCancel={handleModalClose}
          onSave={handleModalSave}
        />
      </Spin>
    )
  }

  return (
    <Spin spinning={loading}>
      <div className="listing-digital-waiver">
        <CustomForm
          initialValues={{ waivers: selectedWaivers }}
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={handleValuesChange}
        >
          <Card>
            <Flex justify="space-between" wrap className="mb-m" gap={10}>
              <Title level={3} >
                {t("listings.tabs.digital_waiver.digital_waiver_information")}
              </Title>
              <Button color="primary" variant="solid" onClick={handleAddDigitalWaiver}>
                {t("listings.tabs.digital_waiver.add_digital_waiver")}
              </Button>
            </Flex>

            <Form.Item name="waivers">
              <Flex>
                <Text>{t("listings.tabs.digital_waiver.add_digital_waiver_document")}</Text>
              </Flex>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select waiver documents"
                options={dropdownOptions}
                optionRender={WaiverOptionRender}
                value={selectedWaivers}
                tagRender={renderWaiverTag}
                onChange={(waivers) => {
                  if (waivers.length > 5) {
                    toaster.error("You can attach a maximum of 5 waivers.")
                    return
                  }
                  form.setFieldsValue({ waivers })
                  setSelectedWaivers(waivers)
                  dispatch(setFormTouched("digital_waiver", true))
                }}
              />
            </Form.Item>
            {errorMessage && <Text type="danger">{errorMessage}</Text>}
          </Card>
          <ListingActionButtons form={form} onFinish={onFinish} />
        </CustomForm>
        {!isMobile && isModalOpen && (
          <DocumentsAddedModal
            isLoading={false}
            setIsLoading={() => {}}
            visible={isModalOpen}
            onCancel={handleModalClose}
            onSave={handleModalSave}
          />
        )}
      </div>
    </Spin>
  )
}

export default ListingDigitalWaiver
